import React, { useContext, useEffect } from 'react';

import { LoadingOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';

import { ModalContext } from '../../../context/ModalContext';
import useGetPaymentDetails from '../../../hooks/trackPackage/useGetPaymentDetails';
import { ITrackPackageResponse } from '../../../models/trackPage/trackPackage.model';
import { formatBadge, formatNaira } from '../../../utils/utils';
import { PrimaryBlackButton } from '../../Button/Buttons';
import ModalContainer from '../ModalContainer';

interface IProcessPackage {
  trackingInfo?: ITrackPackageResponse;
}

const PaymentDetailsModal: React.FC<IProcessPackage> = ({ trackingInfo }) => {
  const modalContext = useContext(ModalContext);
  const { getPaymentDetails, paymentDetailsLoading, paymentDetails } =
    useGetPaymentDetails();

  useEffect(() => {
    if (trackingInfo?.packageId) {
      getPaymentDetails(trackingInfo.packageId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackingInfo?.packageId]);

  const pendingEntries = paymentDetails?.pendingPayment
    ? Object.entries(paymentDetails.pendingPayment)
    : [];

  return (
    <ModalContainer label='Payment Details' description='Payment breakdown'>
      <Container>
        <Title>
          <span>Fee Type</span> <span>Amount</span>
        </Title>
        <DetailsGrid>
          {paymentDetailsLoading ? (
            <LoadingRow>
              <LoadingOutlined spin />
              <span>Loading payment details...</span>
            </LoadingRow>
          ) : pendingEntries.length > 0 ? (
            <>
              {pendingEntries.map(([key, value]) => (
                <DetailItem key={key}>
                  <DetailLabel>{formatBadge(key)}</DetailLabel>
                  <DetailValue>{formatNaira(value)}</DetailValue>
                </DetailItem>
              ))}
              <DetailItem className='total-row'>
                <DetailLabel>
                  <strong>Total</strong>
                </DetailLabel>
                <DetailValue>
                  {formatNaira(paymentDetails?.totalPendingAmount ?? 0)}
                </DetailValue>
              </DetailItem>
            </>
          ) : (
            <EmptyRow>No payment details available.</EmptyRow>
          )}
        </DetailsGrid>
      </Container>
      <PrimaryBlackButton onClick={() => modalContext?.setShowModal(false)}>
        Close
      </PrimaryBlackButton>
    </ModalContainer>
  );
};

export default PaymentDetailsModal;

const Container = styled.div`
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  margin-top: 12px;
  margin-bottom: 12px;
  overflow: clip;
`;

const Title = styled.div`
  font-size: 18px;
  color: #000000;
  font-weight: 600;
  padding: 18px;
  background-color: #fafafa;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${({ theme }) => theme.palette.borderColor};
`;

const DetailsGrid = styled.div`
  padding: 0px 18px;
  grid-template-columns: 1fr;
  gap: 16px;
  & > :nth-last-child(1) {
    border-bottom: none !important;
  }
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${({ theme }) => theme.palette.borderColor};
  padding: 12px 0px;
`;

const DetailLabel = styled.div`
  font-size: 16px;
  font-weight: 400;
  color: #4e4e4e;
`;

const DetailValue = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #0a0a0a;
`;

const LoadingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 24px;
  font-size: 15px;
  color: #4e4e4e;
`;

const EmptyRow = styled.div`
  padding: 24px;
  text-align: center;
  font-size: 15px;
  color: #4e4e4e;
`;
